package org.gymly.ui.screens

import org.gymly.util.GymlyCurrency

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.gymly.data.PickedMediaHolder
import org.gymly.data.PortalRepo
import org.gymly.model.PickedImagePayload
import org.gymly.model.PortalStoreOrder
import org.gymly.model.PortalProofRules
import org.gymly.model.PortalStoreOrderStatus
import org.gymly.model.PortalStorePayMethod
import org.gymly.ui.components.MethodChipGrid
import org.gymly.ui.components.ProofPreview
import org.gymly.ui.components.storeMethodIcon
import androidx.compose.material3.OutlinedButton
import androidx.compose.foundation.layout.heightIn
import org.gymly.model.PortalStoreCategory
import org.gymly.model.PortalStoreProduct
import org.gymly.theme.GymlyColors
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.GymlyLoadingCenter
import org.gymly.ui.components.GymlySecondaryButton
import org.gymly.ui.components.MobilePageHeader
import org.gymly.ui.components.ProofAttachmentView
import org.gymly.ui.components.FieldLabel
import org.gymly.ui.components.GymlyTextField
import org.gymly.ui.components.IsoCalendarDialog
import org.gymly.util.formatIsoToDisplayDate
import org.gymly.util.todayIsoDate
import org.gymly.util.formatMoney
import org.gymly.util.openProofFilePicker
import org.gymly.util.userMessage

private const val ORDERS_PER_PAGE = 10
private const val CATALOG_PAGE_SIZE = 20

@Composable
fun StoreScreen(
    mobile: Boolean = false,
    unreadCount: Long = 0,
    langEs: Boolean = true,
    onLangEs: (Boolean) -> Unit = {},
    onBell: () -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    var orders by remember { mutableStateOf<List<PortalStoreOrder>>(emptyList()) }
    var cart by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }
    // Productos de las líneas del carrito: el catálogo se pagina, así que el carrito guarda lo que necesita.
    var cartProducts by remember { mutableStateOf<Map<String, PortalStoreProduct>>(emptyMap()) }
    var catalogItems by remember { mutableStateOf<List<PortalStoreProduct>>(emptyList()) }
    var categories by remember { mutableStateOf<List<PortalStoreCategory>>(emptyList()) }
    var query by remember { mutableStateOf("") }
    var categoryId by remember { mutableStateOf<String?>(null) }
    var catalogPage by remember { mutableStateOf(0) }
    var catalogHasMore by remember { mutableStateOf(false) }
    var catalogTotal by remember { mutableStateOf(0L) }
    var catalogLoading by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    val uriHandler = androidx.compose.ui.platform.LocalUriHandler.current
    // Pedido cobrado con pasarela: se abrió su página de pago y falta verificar al volver.
    var gatewayOrder by remember { mutableStateOf<PortalStoreOrder?>(null) }
    var checkoutOpen by remember { mutableStateOf(false) }
    var confirming by remember { mutableStateOf(false) }
    var payMethod by remember { mutableStateOf(PortalStorePayMethod.MOBILE_PAYMENT) }
    var ordersPage by remember { mutableStateOf(0) }
    var proofName by remember { mutableStateOf<String?>(null) }
    var reference by remember { mutableStateOf("") }
    var paymentDate by remember { mutableStateOf(todayIsoDate()) }
    // Aviso de referencia repetida en un pago o venta de hoy: solo informa, el pedido se puede confirmar igual.
    var referenceWarning by remember { mutableStateOf<String?>(null) }
    LaunchedEffect(checkoutOpen, payMethod, reference) {
        referenceWarning = null
        val ref = reference.trim()
        if (!checkoutOpen || !payMethod.requiresProof() || ref.length < 5) return@LaunchedEffect
        delay(500)
        val duplicate = runCatching {
            PortalRepo.api.checkPaymentReference(ref, org.gymly.util.todayIsoDate())
        }.getOrDefault(false)
        referenceWarning = if (duplicate) {
            "Esta referencia ya está registrada en un pago de hoy. Puedes confirmar igual; el gimnasio lo revisará."
        } else {
            null
        }
    }
    val proofHolder = remember { PickedMediaHolder() }
    var successOrder by remember { mutableStateOf<PortalStoreOrder?>(null) }
    var detailOrder by remember { mutableStateOf<PortalStoreOrder?>(null) }

    fun clearProof() {
        proofHolder.clear()
        proofName = null
    }

    fun pickProof() {
        openProofFilePicker { picked ->
            if (picked == null) {
                proofHolder.clear()
                scope.launch { error = "No se seleccionó ningún archivo." }
                return@openProofFilePicker
            }
            val payload = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
            proofHolder.set(payload, picked.fileName)
            scope.launch {
                proofName = picked.fileName
                error = null
            }
        }
    }

    fun load() {
        scope.launch {
            error = null
            try {
                orders = PortalRepo.api.fetchStoreOrders()
                // Al volver de Stripe o PayPal, el servidor confirma los pedidos con pasarela que quedaron
                // pendientes. Si alguno ya está pagado, se muestra y la lista se actualiza.
                val pendientes = orders.filter {
                    it.status == PortalStoreOrderStatus.PENDING && (it.method == "CARD" || it.method == "PAYPAL")
                }
                var pagado: PortalStoreOrder? = null
                for (pedido in pendientes) {
                    val actualizado = runCatching { PortalRepo.api.confirmStoreCheckout(pedido.id) }.getOrNull()
                    if (actualizado?.status == PortalStoreOrderStatus.PAID) pagado = actualizado
                }
                if (pagado != null) {
                    successOrder = pagado
                    orders = PortalRepo.api.fetchStoreOrders()
                }
            } catch (e: Exception) {
                error = e.userMessage("Error al cargar la tienda.")
            } finally {
                loading = false
            }
        }
    }

    // Página del catálogo. Escritorio reemplaza la página; celular agrega con "Cargar más".
    suspend fun loadCatalog(page: Int, append: Boolean) {
        catalogLoading = true
        try {
            val result = PortalRepo.api.searchStoreProducts(query.trim(), categoryId, page, CATALOG_PAGE_SIZE)
            catalogItems = if (append) catalogItems + result.items else result.items
            catalogPage = result.page
            catalogHasMore = result.hasMore
            catalogTotal = result.totalCount
            // Las categorías solo vienen en la página 0; se conservan al paginar.
            if (page == 0) categories = result.categories
        } finally {
            catalogLoading = false
        }
    }

    fun runCatalog(block: suspend () -> Unit) {
        scope.launch {
            try {
                block()
            } catch (e: Exception) {
                error = e.userMessage("No se pudo cargar el catálogo.")
            }
        }
    }

    fun setQty(productId: String, qty: Int, maxStock: Int) {
        cart = cart.toMutableMap().also { map ->
            when {
                qty <= 0 -> map.remove(productId)
                else -> map[productId] = qty.coerceAtMost(maxStock.coerceAtLeast(1))
            }
        }
    }

    fun addOne(product: PortalStoreProduct) {
        val current = cart[product.id] ?: 0
        if (current >= product.stockQty) return
        cartProducts = cartProducts + (product.id to product)
        setQty(product.id, current + 1, product.stockQty)
    }

    fun checkout() {
        if (cart.isEmpty() || confirming) return
        scope.launch {
            if (paymentDate.isBlank()) {
                error = "Indica la fecha de pago."
                return@launch
            }
            if (payMethod.isGateway()) {
                // Tarjeta o PayPal: el pedido se crea y se abre la página de la pasarela. Lo pagado lo confirma el servidor.
                confirming = true
                error = null
                try {
                    val order = PortalRepo.api.checkoutStore(
                        method = payMethod,
                        items = cart.map { it.key to it.value },
                        reference = "",
                        paymentDate = paymentDate,
                    )
                    val link = PortalRepo.api.startStoreCheckout(order.id)
                    cart = emptyMap()
                    cartProducts = emptyMap()
                    checkoutOpen = false
                    gatewayOrder = order
                    uriHandler.openUri(link)
                    load()
                } catch (e: Exception) {
                    error = e.userMessage("No se pudo iniciar el pago.")
                } finally {
                    confirming = false
                }
                return@launch
            }
            if (payMethod.requiresProof() && reference.isBlank()) {
                error = "Indica el número de referencia del pago."
                return@launch
            }
            if (!isReferenceValid(payMethod.name, reference.trim())) {
                error = "La referencia de pago móvil debe tener 5 dígitos (los últimos del comprobante)."
                return@launch
            }
            if (payMethod.requiresProof() && proofHolder.payload == null) {
                error = "Adjunta el comprobante (imagen o PDF) para este método de pago."
                return@launch
            }
            confirming = true
            error = null
            try {
                var order = PortalRepo.api.checkoutStore(
                    method = payMethod,
                    items = cart.map { it.key to it.value },
                    reference = reference.trim(),
                    paymentDate = paymentDate,
                )
                val proof = proofHolder.payload
                if (proof != null) {
                    order = PortalRepo.api.uploadStoreOrderProof(order.id, proof)
                    if (order.proofUrl.isNullOrBlank()) {
                        error = "El pedido se creó pero el comprobante no quedó guardado."
                        load()
                        return@launch
                    }
                }
                cart = emptyMap()
                cartProducts = emptyMap()
                checkoutOpen = false
                reference = ""
                clearProof()
                successOrder = order
                load()
            } catch (e: Exception) {
                error = e.userMessage("No se pudo confirmar el pedido.")
            } finally {
                confirming = false
            }
        }
    }

    // Búsqueda y categoría: al escribir espera 300 ms; cada cambio vuelve a la página 0.
    // Dentro del efecto: si cambia la búsqueda antes de responder, la carga anterior se cancela.
    LaunchedEffect(query, categoryId) {
        if (query.isNotBlank()) delay(300)
        try {
            loadCatalog(0, append = false)
        } catch (e: Exception) {
            error = e.userMessage("No se pudo cargar el catálogo.")
        }
    }

    LaunchedEffect(Unit) {
        runCatching { PortalProofRules.rules = PortalRepo.api.fetchPaymentProofRules() }
        load()
    }

    if (loading && catalogItems.isEmpty() && orders.isEmpty()) {
        GymlyLoadingCenter()
        return
    }

    val cartCount = cart.values.sum()
    val cartTotalCents = cart.entries.sumOf { (id, qty) -> (cartProducts[id]?.priceCents ?: 0L) * qty }
    val currency = cartProducts.values.firstOrNull()?.currency
        ?: catalogItems.firstOrNull()?.currency
        ?: GymlyCurrency.CODE
    val storeSubtitle = "Mercancía, equipamiento y alimentos/suplementos — retiro en recepción."

    BoxWithConstraints(Modifier.fillMaxSize().background(GymlyColors.Surface)) {
        val columns = when {
            mobile -> 1
            maxWidth < 600.dp -> 1
            maxWidth < 1000.dp -> 2
            else -> 3
        }
        val compactHeader = mobile || maxWidth < 700.dp

        Column(Modifier.fillMaxSize()) {
            if (mobile) {
                MobilePageHeader(
                    title = "Tienda",
                    subtitle = storeSubtitle,
                    unreadCount = unreadCount,
                    langEs = langEs,
                    onLangEs = onLangEs,
                    onBell = onBell,
                )
            }

            Column(
                Modifier
                    .weight(1f)
                    .verticalScroll(rememberScrollState())
                    .padding(GymlySpace.lg),
            ) {
                if (!mobile) {
                    if (compactHeader) {
                        Text("Tienda", fontWeight = FontWeight.Bold, fontSize = 28.sp, color = GymlyColors.Text)
                        Spacer(Modifier.height(6.dp))
                        Text(storeSubtitle, color = GymlyColors.TextMuted, fontSize = 14.sp)
                        if (cartCount > 0) {
                            Spacer(Modifier.height(12.dp))
                            Button(
                                onClick = { checkoutOpen = true },
                                colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Primary),
                                shape = RoundedCornerShape(10.dp),
                                modifier = Modifier.fillMaxWidth(),
                            ) {
                                Text(
                                    "Ver carrito ($cartCount) · ${formatMoney(cartTotalCents.toInt(), currency)}",
                                    fontWeight = FontWeight.SemiBold,
                                )
                            }
                        }
                    } else {
                        Row(
                            Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.Top,
                        ) {
                            Column(Modifier.weight(1f).padding(end = 16.dp)) {
                                Text("Tienda", fontWeight = FontWeight.Bold, fontSize = 28.sp, color = GymlyColors.Text)
                                Spacer(Modifier.height(6.dp))
                                Text(storeSubtitle, color = GymlyColors.TextMuted, fontSize = 14.sp)
                            }
                            if (cartCount > 0) {
                                Button(
                                    onClick = { checkoutOpen = true },
                                    colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Primary),
                                    shape = RoundedCornerShape(10.dp),
                                ) {
                                    Text(
                                        "Ver carrito ($cartCount) · ${formatMoney(cartTotalCents.toInt(), currency)}",
                                        fontWeight = FontWeight.SemiBold,
                                    )
                                }
                            }
                        }
                    }
                }

                if (!mobile) Spacer(Modifier.height(16.dp))
                error?.let {
                    Text(it, color = GymlyColors.Error, modifier = Modifier.padding(bottom = 10.dp))
                }

                GymlyTextField(
                    value = query,
                    onValueChange = { query = it },
                    placeholder = "Buscar producto por nombre o descripción",
                )
                Spacer(Modifier.height(8.dp))
                CategoryChips(
                    categories = categories,
                    selected = categoryId,
                    onSelect = { categoryId = it },
                )
                Spacer(Modifier.height(12.dp))

                if (catalogItems.isEmpty() && !catalogLoading) {
                    Text(
                        if (query.isBlank() && categoryId == null) {
                            "No hay productos disponibles por ahora."
                        } else {
                            "Sin resultados para la búsqueda."
                        },
                        color = GymlyColors.TextMuted,
                    )
                } else if (mobile) {
                    // Celular: filas compactas; "Cargar más" agrega la siguiente página.
                    catalogItems.forEach { product ->
                        StoreProductMobileRow(
                            product = product,
                            qty = cart[product.id] ?: 0,
                            onAdd = { addOne(product) },
                            onMinus = { setQty(product.id, (cart[product.id] ?: 0) - 1, product.stockQty) },
                            onPlus = { addOne(product) },
                        )
                        Spacer(Modifier.height(8.dp))
                    }
                    if (catalogHasMore) {
                        GymlySecondaryButton(
                            text = "Cargar más productos",
                            loading = catalogLoading,
                            onClick = { runCatalog { loadCatalog(catalogPage + 1, append = true) } },
                        )
                    }
                } else {
                    // Escritorio: tabla con una fila por producto y paginación anterior/siguiente.
                    StoreProductTable(
                        products = catalogItems,
                        cart = cart,
                        onAdd = ::addOne,
                        onMinus = { setQty(it.id, (cart[it.id] ?: 0) - 1, it.stockQty) },
                        onPlus = ::addOne,
                    )
                    CatalogPager(
                        page = catalogPage,
                        totalPages = ((catalogTotal + CATALOG_PAGE_SIZE - 1) / CATALOG_PAGE_SIZE).toInt(),
                        loading = catalogLoading,
                        onPage = { target -> runCatalog { loadCatalog(target, append = false) } },
                    )
                }

                // Mis pagos: solo ventas pagadas (del portal y del web admin). Sin pagos, no aparece.
                val paidPurchases = orders.filter { it.status == PortalStoreOrderStatus.PAID }
                if (paidPurchases.isNotEmpty()) {
                    Spacer(Modifier.height(28.dp))
                    Text("Mis compras", fontWeight = FontWeight.Bold, fontSize = 22.sp, color = GymlyColors.Text)
                    Spacer(Modifier.height(12.dp))
                    // Solo lectura: 10 compras por página; la lupa/toque abre el detalle.
                    val pageCount = (paidPurchases.size + ORDERS_PER_PAGE - 1) / ORDERS_PER_PAGE
                    val page = ordersPage.coerceIn(0, pageCount - 1)
                    paidPurchases.drop(page * ORDERS_PER_PAGE).take(ORDERS_PER_PAGE).forEach { order ->
                        StoreOrderRow(order, onClick = { detailOrder = order })
                        Spacer(Modifier.height(10.dp))
                    }
                    if (pageCount > 1) {
                        Row(
                            Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.Center,
                        ) {
                            TextButton(onClick = { ordersPage = page - 1 }, enabled = page > 0) {
                                Text("Anterior")
                            }
                            Text(
                                "${page + 1} / $pageCount",
                                color = GymlyColors.TextMuted,
                                fontSize = 13.sp,
                            )
                            TextButton(onClick = { ordersPage = page + 1 }, enabled = page < pageCount - 1) {
                                Text("Siguiente")
                            }
                        }
                    }
                }

                // Pagos por validar: ventas con comprobante que aún no se validan. Al validarse pasan a Mis pagos.
                val porValidar = orders.filter { it.status == PortalStoreOrderStatus.POR_VALIDAR }
                if (porValidar.isNotEmpty()) {
                    Spacer(Modifier.height(28.dp))
                    Text("Compras por validar", fontWeight = FontWeight.Bold, fontSize = 22.sp, color = GymlyColors.Text)
                    Spacer(Modifier.height(12.dp))
                    porValidar.forEach { order ->
                        StoreOrderRow(order, onClick = { detailOrder = order })
                        Spacer(Modifier.height(10.dp))
                    }
                }
                Spacer(Modifier.height(GymlySpace.xl))
            }
            // Celular: barra fija abajo con el carrito, siempre visible mientras se elige.
            if (mobile && cartCount > 0) {
                Button(
                    onClick = { checkoutOpen = true },
                    colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Primary),
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = GymlySpace.lg, vertical = 10.dp),
                ) {
                    Text(
                        "Carrito ($cartCount) · ${formatMoney(cartTotalCents.toInt(), currency)} — Ver",
                        fontWeight = FontWeight.SemiBold,
                    )
                }
            }
        }
    }

    if (checkoutOpen) {
        CheckoutDialog(
            products = cartProducts.values.toList(),
            cart = cart,
            method = payMethod,
            proofName = proofName,
            reference = reference,
            onReference = { reference = it },
            paymentDate = paymentDate,
            onPaymentDate = { paymentDate = it },
            referenceWarning = referenceWarning,
            confirming = confirming,
            onMethod = { payMethod = it },
            onPickProof = { pickProof() },
            proofPayload = proofHolder.payload,
            onRemoveProof = { clearProof() },
            onDismiss = { if (!confirming) { checkoutOpen = false; clearProof() } },
            onConfirm = { checkout() },
        )
    }

    gatewayOrder?.let { order ->
        AlertDialog(
            onDismissRequest = { if (!confirming) gatewayOrder = null },
            title = { Text("Completa el pago", fontWeight = FontWeight.Bold) },
            text = {
                Text(
                    "Se abrió la página de pago de ${order.methodLabel}. Cuando termines, vuelve aquí y pulsa " +
                        "Verificar pago. Pedido #${order.saleNumber}.",
                )
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            confirming = true
                            error = null
                            try {
                                val updated = PortalRepo.api.confirmStoreCheckout(order.id)
                                if (updated.status == PortalStoreOrderStatus.PAID) {
                                    gatewayOrder = null
                                    successOrder = updated
                                } else {
                                    error = "Todavía no vemos el pago. Si ya pagaste, espera un momento y vuelve a verificar."
                                }
                                load()
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo verificar el pago.")
                            } finally {
                                confirming = false
                            }
                        }
                    },
                    enabled = !confirming,
                    colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Primary),
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Text("Verificar pago")
                }
            },
            dismissButton = {
                TextButton(onClick = { gatewayOrder = null }, enabled = !confirming) { Text("Más tarde") }
            },
        )
    }

    successOrder?.let { order ->
        val paid = order.status == PortalStoreOrderStatus.PAID
        AlertDialog(
            onDismissRequest = { successOrder = null },
            title = {
                Text(if (paid) "Pago confirmado" else "Pedido registrado")
            },
            text = {
                Column {
                    Text(
                        if (paid) {
                            "Tu pedido está pagado — retira en recepción."
                        } else {
                            "Tu pedido #${order.saleNumber} quedó pendiente de verificación. Retira en recepción cuando esté pagado."
                        },
                    )
                    if (!order.proofUrl.isNullOrBlank()) {
                        Spacer(Modifier.height(12.dp))
                        ProofAttachmentView(
                            proofUrl = order.proofUrl,
                            proofContentType = order.proofContentType,
                            label = "Comprobante",
                            accent = GymlyColors.Primary,
                            muted = GymlyColors.TextMuted,
                            surface = GymlyColors.Surface,
                            text = GymlyColors.Text,
                            outline = GymlyColors.Outline,
                            compact = true,
                        )
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = { successOrder = null },
                    colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Primary),
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Text("Cerrar")
                }
            },
        )
    }

    detailOrder?.let { order ->
        AlertDialog(
            onDismissRequest = { detailOrder = null },
            title = { Text("Pedido #${order.saleNumber}", fontWeight = FontWeight.Bold) },
            text = {
                Column(Modifier.verticalScroll(rememberScrollState())) {
                    Text(
                        formatMoney(order.totalCents.toInt(), order.currency),
                        fontWeight = FontWeight.Bold,
                        fontSize = 18.sp,
                        color = GymlyColors.Text,
                    )
                    Spacer(Modifier.height(6.dp))
                    Text(order.status.badgeLabel(), color = GymlyColors.TextMuted, fontSize = 13.sp)
                    Spacer(Modifier.height(8.dp))
                    Text(
                        order.itemsSummary.ifBlank {
                            order.items.joinToString(", ") { "${it.productName} x${it.qty}" }
                        },
                        color = GymlyColors.Text,
                        fontSize = 14.sp,
                    )
                    if (order.methodLabel.isNotBlank()) {
                        Spacer(Modifier.height(6.dp))
                        Text("Método: ${order.methodLabel}", color = GymlyColors.TextMuted, fontSize = 13.sp)
                    }
                    if (!order.proofUrl.isNullOrBlank()) {
                        Spacer(Modifier.height(14.dp))
                        ProofAttachmentView(
                            proofUrl = order.proofUrl,
                            proofContentType = order.proofContentType,
                            label = "Comprobante",
                            accent = GymlyColors.Primary,
                            muted = GymlyColors.TextMuted,
                            surface = GymlyColors.Surface,
                            text = GymlyColors.Text,
                            outline = GymlyColors.Outline,
                        )
                    } else {
                        Spacer(Modifier.height(10.dp))
                        Text("Sin comprobante adjunto.", color = GymlyColors.TextMuted, fontSize = 13.sp)
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = { detailOrder = null }) { Text("Cerrar") }
            },
        )
    }
}

@Composable
private fun CategoryChips(
    categories: List<PortalStoreCategory>,
    selected: String?,
    onSelect: (String?) -> Unit,
) {
    if (categories.isEmpty()) return
    Row(
        Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        CategoryChip(label = "Todas", active = selected == null, onClick = { onSelect(null) })
        categories.forEach { category ->
            CategoryChip(
                label = category.name,
                active = selected == category.id,
                onClick = { onSelect(category.id) },
            )
        }
    }
}

@Composable
private fun CategoryChip(label: String, active: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .clip(RoundedCornerShape(20.dp))
            .background(if (active) GymlyColors.Primary else GymlyColors.SurfaceContainer)
            .border(1.dp, GymlyColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(20.dp))
            .clickable(onClick = onClick)
            .padding(horizontal = 14.dp, vertical = 7.dp),
    ) {
        Text(
            label,
            fontSize = 13.sp,
            fontWeight = FontWeight.SemiBold,
            color = if (active) Color.White else GymlyColors.Text,
        )
    }
}

@Composable
private fun StoreProductTable(
    products: List<PortalStoreProduct>,
    cart: Map<String, Int>,
    onAdd: (PortalStoreProduct) -> Unit,
    onMinus: (PortalStoreProduct) -> Unit,
    onPlus: (PortalStoreProduct) -> Unit,
) {
    Column(Modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(horizontal = 14.dp, vertical = 6.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            TableHeaderCell("Producto", Modifier.weight(3f))
            TableHeaderCell("Categoría", Modifier.weight(2f))
            TableHeaderCell("Precio Bs", Modifier.weight(1.5f))
            TableHeaderCell("Precio $", Modifier.weight(1f))
            TableHeaderCell("Stock", Modifier.weight(1f))
            TableHeaderCell("Cantidad", Modifier.width(150.dp))
        }
        products.forEach { product ->
            val qty = cart[product.id] ?: 0
            Row(
                Modifier
                    .fillMaxWidth()
                    .padding(bottom = 6.dp)
                    .clip(RoundedCornerShape(10.dp))
                    .background(GymlyColors.SurfaceContainer)
                    .border(1.dp, GymlyColors.Outline.copy(alpha = 0.45f), RoundedCornerShape(10.dp))
                    .padding(horizontal = 14.dp, vertical = 10.dp),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(Modifier.weight(3f)) {
                    Text(
                        product.name,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 14.sp,
                        color = GymlyColors.Text,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                    product.description?.takeIf { it.isNotBlank() }?.let {
                        Text(
                            it,
                            color = GymlyColors.TextMuted,
                            fontSize = 12.sp,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                    }
                }
                TableText(product.categoryLabel, GymlyColors.TextMuted, Modifier.weight(2f))
                TableText(formatMoney(product.priceCents.toInt(), product.currency), GymlyColors.Text, Modifier.weight(1.5f))
                TableText(
                    if (product.priceUsdCents > 0L) formatMoney(product.priceUsdCents.toInt(), "USD") else "—",
                    GymlyColors.Text,
                    Modifier.weight(1f),
                )
                TableText(
                    if (product.stockQty <= 0) "Agotado" else product.stockQty.toString(),
                    if (product.stockQty <= 0) GymlyColors.Error else GymlyColors.Text,
                    Modifier.weight(1f),
                )
                Box(Modifier.width(150.dp)) {
                    if (qty <= 0) {
                        Button(
                            onClick = { onAdd(product) },
                            enabled = product.stockQty > 0,
                            colors = ButtonDefaults.buttonColors(
                                containerColor = GymlyColors.Outline.copy(alpha = 0.35f),
                                contentColor = GymlyColors.Text,
                                disabledContainerColor = GymlyColors.Outline.copy(alpha = 0.2f),
                            ),
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.fillMaxWidth(),
                        ) {
                            Text("Agregar", fontWeight = FontWeight.SemiBold)
                        }
                    } else {
                        Row(
                            Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(6.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            QtyButton(label = "−", onClick = { onMinus(product) }, modifier = Modifier.weight(1f))
                            Text(
                                qty.toString(),
                                fontWeight = FontWeight.Bold,
                                fontSize = 15.sp,
                                textAlign = TextAlign.Center,
                                modifier = Modifier.width(28.dp),
                            )
                            QtyButton(
                                label = "+",
                                onClick = { onPlus(product) },
                                enabled = qty < product.stockQty,
                                modifier = Modifier.weight(1f),
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TableHeaderCell(text: String, modifier: Modifier) {
    Text(
        text,
        modifier = modifier,
        color = GymlyColors.TextMuted,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
    )
}

@Composable
private fun TableText(text: String, color: Color, modifier: Modifier) {
    Text(
        text,
        modifier = modifier,
        color = color,
        fontSize = 13.sp,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
    )
}

@Composable
private fun CatalogPager(
    page: Int,
    totalPages: Int,
    loading: Boolean,
    onPage: (Int) -> Unit,
) {
    if (totalPages <= 1) return
    Row(
        Modifier.fillMaxWidth().padding(top = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center,
    ) {
        TextButton(onClick = { onPage(page - 1) }, enabled = page > 0 && !loading) { Text("Anterior") }
        pageWindow(page, totalPages).forEach { item ->
            if (item < 0) {
                Text("…", color = GymlyColors.TextMuted, modifier = Modifier.padding(horizontal = 4.dp))
            } else {
                val active = item == page
                Box(
                    Modifier
                        .padding(horizontal = 2.dp)
                        .size(36.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(if (active) GymlyColors.Primary else Color.Transparent)
                        .clickable(enabled = !loading && !active) { onPage(item) },
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        "${item + 1}",
                        fontSize = 13.sp,
                        fontWeight = if (active) FontWeight.Bold else FontWeight.Normal,
                        color = if (active) Color.White else GymlyColors.Text,
                    )
                }
            }
        }
        TextButton(onClick = { onPage(page + 1) }, enabled = page < totalPages - 1 && !loading) { Text("Siguiente") }
    }
}

/** Páginas a mostrar (índices base 0); -1 marca los puntos suspensivos. Siempre muestra primera, última y vecinas de la actual. */
internal fun pageWindow(current: Int, total: Int): List<Int> {
    if (total <= 7) return (0 until total).toList()
    val shown = listOf(0, total - 1, current - 1, current, current + 1).filter { it in 0 until total }.distinct().sorted()
    val result = mutableListOf<Int>()
    var previous = -1
    shown.forEach { p ->
        if (p - previous > 1) result += -1
        result += p
        previous = p
    }
    return result
}

@Composable
private fun StoreProductMobileRow(
    product: PortalStoreProduct,
    qty: Int,
    onAdd: () -> Unit,
    onMinus: () -> Unit,
    onPlus: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .background(GymlyColors.SurfaceContainer)
            .border(1.dp, GymlyColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(14.dp))
            .padding(horizontal = 16.dp, vertical = 14.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.weight(1f).padding(end = 12.dp)) {
            Text(product.name, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = GymlyColors.Text)
            Spacer(Modifier.height(4.dp))
            Text(
                "${product.categoryLabel} · ${storePriceLabel(product)}",
                color = GymlyColors.TextMuted,
                fontSize = 13.sp,
            )
            ProductSpecs(product)
        }
        if (qty <= 0) {
            Button(
                onClick = onAdd,
                enabled = product.stockQty > 0,
                colors = ButtonDefaults.buttonColors(
                    containerColor = GymlyColors.Outline.copy(alpha = 0.35f),
                    contentColor = GymlyColors.Text,
                    disabledContainerColor = GymlyColors.Outline.copy(alpha = 0.2f),
                ),
                shape = RoundedCornerShape(10.dp),
            ) {
                Text("Agregar", fontWeight = FontWeight.SemiBold)
            }
        } else {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                QtyButton(label = "−", onClick = onMinus, modifier = Modifier.width(40.dp))
                Text(
                    qty.toString(),
                    fontWeight = FontWeight.Bold,
                    fontSize = 16.sp,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.width(24.dp),
                )
                QtyButton(
                    label = "+",
                    onClick = onPlus,
                    enabled = qty < product.stockQty,
                    modifier = Modifier.width(40.dp),
                )
            }
        }
    }
}

@Composable
private fun QtyButton(
    label: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
) {
    Box(
        modifier
            .height(40.dp)
            .clip(RoundedCornerShape(10.dp))
            .background(
                if (enabled) GymlyColors.Outline.copy(alpha = 0.35f)
                else GymlyColors.Outline.copy(alpha = 0.18f),
            )
            .clickable(enabled = enabled, onClick = onClick),
        contentAlignment = Alignment.Center,
    ) {
        Text(label, fontWeight = FontWeight.Bold, fontSize = 18.sp, color = GymlyColors.Text)
    }
}

@Composable
private fun StoreOrderRow(order: PortalStoreOrder, onClick: () -> Unit) {
    val statusColor = when (order.status) {
        PortalStoreOrderStatus.PAID -> Color(0xFF2E7D32)
        PortalStoreOrderStatus.PENDING -> Color(0xFFEF6C00)
        PortalStoreOrderStatus.FAILED -> GymlyColors.Error
        PortalStoreOrderStatus.POR_VALIDAR -> Color(0xFF1565C0)
    }
    val statusBg = when (order.status) {
        PortalStoreOrderStatus.PAID -> Color(0xFF2E7D32).copy(alpha = 0.12f)
        PortalStoreOrderStatus.PENDING -> Color(0xFFEF6C00).copy(alpha = 0.12f)
        PortalStoreOrderStatus.FAILED -> GymlyColors.Error.copy(alpha = 0.12f)
        PortalStoreOrderStatus.POR_VALIDAR -> Color(0xFF1565C0).copy(alpha = 0.12f)
    }
    Row(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .background(GymlyColors.SurfaceContainer)
            .border(1.dp, GymlyColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(14.dp))
            .clickable(onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 14.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.weight(1f).padding(end = 12.dp)) {
            Text(
                "#${order.saleNumber} · ${formatMoney(order.totalCents.toInt(), order.currency)}",
                fontWeight = FontWeight.SemiBold,
                fontSize = 15.sp,
                color = GymlyColors.Text,
            )
            Spacer(Modifier.height(4.dp))
            Text(
                order.itemsSummary.ifBlank {
                    order.items.joinToString(", ") { "${it.productName} x${it.qty}" }
                },
                color = GymlyColors.TextMuted,
                fontSize = 13.sp,
            )
            if (!order.proofUrl.isNullOrBlank()) {
                Spacer(Modifier.height(4.dp))
                Text("Ver comprobante", color = GymlyColors.Primary, fontSize = 12.sp, fontWeight = FontWeight.Medium)
            }
        }
        Text(
            order.status.badgeLabel(),
            color = statusColor,
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier
                .clip(RoundedCornerShape(8.dp))
                .background(statusBg)
                .padding(horizontal = 10.dp, vertical = 5.dp),
        )
    }
}

@Composable
private fun CheckoutDialog(
    products: List<PortalStoreProduct>,
    cart: Map<String, Int>,
    method: PortalStorePayMethod,
    proofName: String?,
    reference: String,
    onReference: (String) -> Unit,
    paymentDate: String,
    onPaymentDate: (String) -> Unit,
    referenceWarning: String?,
    confirming: Boolean,
    onMethod: (PortalStorePayMethod) -> Unit,
    onPickProof: () -> Unit,
    proofPayload: org.gymly.model.PickedImagePayload? = null,
    onRemoveProof: () -> Unit = {},
    onDismiss: () -> Unit,
    onConfirm: () -> Unit,
) {
    val lines = products.mapNotNull { p ->
        val qty = cart[p.id] ?: return@mapNotNull null
        if (qty <= 0) null else Triple(p.name, qty, p.priceCents * qty)
    }
    val total = lines.sumOf { it.third }
    val currency = products.firstOrNull()?.currency ?: GymlyCurrency.CODE

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text("Finalizar compra", fontWeight = FontWeight.Bold)
        },
        text = {
            Column(Modifier.widthIn(max = 420.dp).verticalScroll(rememberScrollState())) {
                lines.forEach { (name, qty, lineTotal) ->
                    Row(
                        Modifier.fillMaxWidth().padding(bottom = 6.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        Text("$name x$qty", color = GymlyColors.Text, fontSize = 14.sp)
                        Text(formatMoney(lineTotal.toInt(), currency), color = GymlyColors.Text, fontSize = 14.sp)
                    }
                }
                Spacer(Modifier.height(4.dp))
                Row(
                    Modifier.fillMaxWidth().padding(bottom = 14.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Text("Total", fontWeight = FontWeight.Bold, fontSize = 15.sp)
                    Text(
                        formatMoney(total.toInt(), currency),
                        fontWeight = FontWeight.Bold,
                        fontSize = 15.sp,
                    )
                }
                Text("Método", fontWeight = FontWeight.SemiBold, fontSize = 14.sp, color = GymlyColors.Text)
                Spacer(Modifier.height(8.dp))
                FlowMethodPills(selected = method, onSelect = onMethod)
                method.hint()?.let { hint ->
                    Spacer(Modifier.height(10.dp))
                    Text(hint, color = GymlyColors.TextMuted, fontSize = 12.sp)
                }
                Spacer(Modifier.height(12.dp))
                FieldLabel("Fecha de pago")
                var showCalendar by remember { mutableStateOf(false) }
                OutlinedButton(
                    onClick = { showCalendar = true },
                    enabled = !confirming,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Text(formatIsoToDisplayDate(paymentDate), color = GymlyColors.Text)
                }
                if (showCalendar) {
                    IsoCalendarDialog(
                        initialIso = paymentDate,
                        onDismiss = { showCalendar = false },
                        onConfirm = {
                            onPaymentDate(it)
                            showCalendar = false
                        },
                        accent = GymlyColors.Primary,
                    )
                }
                if (method.requiresProof()) {
                    Spacer(Modifier.height(12.dp))
                    FieldLabel(if (method.name == "MOBILE_PAYMENT") "Referencia (últimos 5 dígitos)" else "Número de referencia")
                    GymlyTextField(
                        value = reference,
                        onValueChange = { onReference(referenceInputFor(method.name, it)) },
                        placeholder = if (method.name == "MOBILE_PAYMENT") "Últimos 5 dígitos del comprobante" else "Nº de referencia del pago",
                    )
                    referenceWarning?.let {
                        Spacer(Modifier.height(6.dp))
                        Text(it, color = Color(0xFFEF6C00), fontSize = 12.sp)
                    }
                    Spacer(Modifier.height(12.dp))
                    Text("Comprobante (imagen o PDF)", fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
                    Spacer(Modifier.height(6.dp))
                    ProofPreview(
                        payload = proofPayload,
                        fileName = proofName,
                        enabled = !confirming,
                        onPick = onPickProof,
                        onRemove = onRemoveProof,
                    )
                }
            }
        },
        confirmButton = {
            Button(
                onClick = onConfirm,
                enabled = !confirming && lines.isNotEmpty(),
                colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Primary),
                shape = RoundedCornerShape(10.dp),
            ) {
                if (confirming) {
                    CircularProgressIndicator(
                        Modifier.size(18.dp),
                        color = Color.White,
                        strokeWidth = 2.dp,
                    )
                } else {
                    Text("Confirmar pedido")
                }
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss, enabled = !confirming) {
                Text("Cancelar")
            }
        },
    )
}

@Composable
private fun FlowMethodPills(
    selected: PortalStorePayMethod,
    onSelect: (PortalStorePayMethod) -> Unit,
) {
    // Solo métodos que exigen comprobante (Configuración → Tipos de pago): en la tienda siempre se sube imagen.
    // Tarjeta y PayPal aparecen solo si el backend los marca como disponibles (habilitados y con pasarela lista).
    val methods = PortalStorePayMethod.entries.filter {
        it.requiresProof() || (it.isGateway() && PortalProofRules.rules.containsKey(it.name))
    }
    LaunchedEffect(methods) {
        if (methods.isNotEmpty() && selected !in methods) onSelect(methods.first())
    }
    MethodChipGrid(
        choices = methods,
        selected = selected,
        label = { it.labelEs() },
        icon = ::storeMethodIcon,
        onSelect = onSelect,
    )
}

private fun storePriceLabel(product: PortalStoreProduct): String {
    val bs = formatMoney(product.priceCents.toInt(), product.currency)
    return if (product.priceUsdCents > 0L) "$bs · ${formatMoney(product.priceUsdCents.toInt(), "USD")}" else bs
}

@Composable
private fun ProductSpecs(product: PortalStoreProduct) {
    val lines = listOfNotNull(
        product.description?.takeIf { it.isNotBlank() }?.let { "Descripción: $it" },
        product.ingredients?.takeIf { it.isNotBlank() }?.let { "Ingredientes: $it" },
        product.videoUrl?.takeIf { it.isNotBlank() }?.let { "Video: $it" },
    )
    if (lines.isEmpty()) return
    Spacer(Modifier.height(6.dp))
    lines.forEach { line ->
        Text(line, color = GymlyColors.TextMuted, fontSize = 12.sp)
    }
}
